import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useClients } from '@/hooks/use-clients';
import { ClientFormDialog } from '@/pages/clients/ClientFormDialog';
import type { Client } from '@erp/data-access/interfaces';
import {
  Badge, Button,
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
  Input,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@erp/ui';
import { Plus, Search, UserPlus } from '@/components/icons';
import { SearchableSelect } from '@/components/ui/searchable-select';

interface ClientPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (client: Client) => void;
}

export function ClientPickerDialog({ open, onOpenChange, onSelect }: ClientPickerDialogProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Read the full cached list (one canonical key) and filter client-side so
  // the picker works offline even when the user types or changes the type.
  const { data: allClients, isLoading } = useClients();
  const clients = useMemo(() => {
    if (!allClients) return undefined;
    const s = search.trim().toLowerCase();
    return allClients.filter((c) => {
      if (typeFilter && c.type !== typeFilter) return false;
      if (s) {
        const hay = `${c.name ?? ''} ${c.code ?? ''} ${c.taxId ?? ''} ${c.phone ?? ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [allClients, search, typeFilter]);

  const handleSelect = (client: Client) => {
    onSelect(client);
    onOpenChange(false);
    setSearch('');
    setTypeFilter('');
  };

  const handleCreateClose = () => {
    setShowCreateDialog(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setSearch(''); setTypeFilter(''); } }}>
        <DialogContent className="max-w-6xl max-h-[85vh] flex flex-col gap-5 p-6">
          <DialogHeader>
            <DialogTitle className="text-xl">{t('sales.selectClient')}</DialogTitle>
            <DialogDescription>{t('sales.picker.clientPickerHint')}</DialogDescription>
          </DialogHeader>

          {/* Filters bar */}
          <div className="flex gap-3 items-center">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                placeholder={t('sales.picker.searchClient')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="ps-10 h-10 text-sm"
                autoFocus
              />
            </div>
            <div className="w-48 h-10">
              <SearchableSelect
                options={[{ value: 'company', label: t('clients.typeCompany') }, { value: 'individual', label: t('clients.typeIndividual') }]}
                value={typeFilter}
                onChange={setTypeFilter}
                label={t('clients.type')}
                placeholder={t('common.all')}
                searchPlaceholder={t('common.search')}
                className="h-10"
              />
            </div>
            <Button variant="default" size="default" onClick={() => setShowCreateDialog(true)}>
              <UserPlus size={16} className="me-2" />
              {t('sales.picker.newClient')}
            </Button>
          </div>

          {/* Results count */}
          {!isLoading && clients && (
            <p className="text-xs text-muted-foreground -mt-2">
              {clients.length} {t('sales.picker.resultsFound')}
            </p>
          )}

          {/* Results table */}
          <div className="flex-1 overflow-y-auto border rounded-lg min-h-[300px]">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="py-3 text-sm font-semibold">{t('clients.code')}</TableHead>
                  <TableHead className="py-3 text-sm font-semibold">{t('clients.name')}</TableHead>
                  <TableHead className="py-3 text-sm font-semibold">{t('clients.type')}</TableHead>
                  <TableHead className="py-3 text-sm font-semibold">{t('clients.taxId')}</TableHead>
                  <TableHead className="py-3 text-sm font-semibold">{t('clients.city')}</TableHead>
                  <TableHead className="py-3 text-sm font-semibold">{t('clients.phone')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-16 text-muted-foreground text-sm">
                      {t('common.loading')}
                    </TableCell>
                  </TableRow>
                ) : !clients?.length ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <Search size={32} className="text-muted-foreground/40" />
                        <p className="text-muted-foreground">{t('common.noResults')}</p>
                        <Button variant="outline" size="sm" onClick={() => setShowCreateDialog(true)}>
                          <Plus size={14} className="me-1.5" />
                          {t('sales.picker.newClient')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  clients.map((client) => (
                    <TableRow
                      key={client.id}
                      className="cursor-pointer hover:bg-accent transition-colors"
                      onClick={() => handleSelect(client)}
                    >
                      <TableCell className="text-sm py-3">{client.code}</TableCell>
                      <TableCell className="font-medium text-sm py-3">{client.name}</TableCell>
                      <TableCell className="py-3">
                        <Badge
                          variant="outline"
                          className={client.type === 'company'
                            ? 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-400'
                            : 'text-muted-foreground'}
                        >
                          {client.type === 'company' ? t('clients.typeCompany') : t('clients.typeIndividual')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground py-3">{client.taxId ?? '—'}</TableCell>
                      <TableCell className="text-sm py-3">{client.city ?? '—'}</TableCell>
                      <TableCell className="text-sm py-3">{client.phone ?? '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <ClientFormDialog
        open={showCreateDialog}
        onOpenChange={handleCreateClose}
        onCreated={(newClient) => {
          setShowCreateDialog(false);
          onSelect(newClient);
          onOpenChange(false);
          setSearch('');
          setTypeFilter('all');
        }}
      />
    </>
  );
}
